import React, { useEffect, useState } from 'react';
import axios from 'axios';

const PlatformAdminOrderView = () => {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [logs, setLogs] = useState([]);

  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('id') || '';

  useEffect(() => {
    fetchData();
  }, [window.location.search]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `/api/platform_admin/order_view?id=${encodeURIComponent(orderId)}`,
        { withCredentials: true }
      );

      if (res.data && res.data.ok) {
        setOrder(res.data.order || null);
        setItems(Array.isArray(res.data.items) ? res.data.items : []);
        setDelivery(res.data.delivery || null);
        setLogs(Array.isArray(res.data.logs) ? res.data.logs : []);
      } else if (res.data && res.data.redirect) {
        window.location.href = res.data.redirect;
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        const redirectUrl =
          err.response.data && err.response.data.redirect
            ? err.response.data.redirect
            : '/platform_admin/login';
        window.location.href = redirectUrl;
        return;
      }

      if (err.response && err.response.data && err.response.data.msg) {
        alert(err.response.data.msg);
        if (err.response.status === 400 || err.response.status === 404) {
          window.location.href = '/platform_admin/orders';
          return;
        }
      }

      console.error('주문 상세 로드 실패:', err);
      setOrder(null);
      setItems([]);
      setDelivery(null);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (e) => {
    e.preventDefault();

    try {
      const res = await axios.get('/api/platform_admin/logout', {
        withCredentials: true,
      });

      if (res.data && res.data.ok && res.data.redirect) {
        window.location.href = res.data.redirect;
        return;
      }

      window.location.href = '/platform_admin/login';
    } catch (err) {
      console.error('로그아웃 실패:', err);
      window.location.href = '/platform_admin/login';
    }
  };

  const formatNumber = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString('ko-KR');
  };

  const renderAddressWithBr = (value) => {
    if (!value) return '';
    return String(value)
      .split('\n')
      .map((line, idx, arr) => (
        <React.Fragment key={idx}>
          {line}
          {idx < arr.length - 1 ? <br /> : null}
        </React.Fragment>
      ));
  };

  return (
    <div className="bg-light min-vh-100">
      <nav className="navbar navbar-dark bg-dark px-4">
        <span className="navbar-brand">플랫폼 관리자</span>
        <div>
          <a href="/platform_admin/sellers" className="btn btn-sm btn-outline-light me-2">
            셀러관리
          </a>
          <a href="/platform_admin/shops" className="btn btn-sm btn-outline-light me-2">
            상점관리
          </a>
          <a href="/platform_admin/orders" className="btn btn-sm btn-outline-light active me-2">
            주문관리
          </a>
          <a href="/platform_admin/claims" className="btn btn-sm btn-outline-light me-2">
            클레임
          </a>
          <a href="/platform_admin/logout" onClick={handleLogout} className="btn btn-sm btn-danger">
            로그아웃
          </a>
        </div>
      </nav>

      <div className="container my-4">
        {loading ? (
          <div className="text-center py-5 text-muted">불러오는 중...</div>
        ) : !order ? (
          <div className="text-center py-5 text-muted">주문 정보 없음</div>
        ) : (
          <>
            <h3 className="mb-4">📦 주문 상세 (#{order.id})</h3>

            <a href="/platform_admin/orders" className="btn btn-secondary mb-3">
              ← 주문 목록
            </a>

            <div className="card mb-3">
              <div className="card-header">주문 정보</div>
              <div className="card-body">
                <p><b>상점:</b> {order.shop_name}</p>
                <p><b>결제수단:</b> {order.payment_method}</p>
                <p><b>주문일:</b> {order.order_date}</p>
                <p><b>상태:</b> {order.status}</p>
                <p><b>총액:</b> {formatNumber(order.total_price)}원</p>
                <p><b>결제금액:</b> {formatNumber(order.result_price)}원</p>
                <p><b>PaymentKey:</b> {order.paymentkey}</p>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header">구매자 정보</div>
              <div className="card-body">
                <p><b>이름:</b> {order.buyer_name}</p>
                <p><b>전화:</b> {order.buyer_phone}</p>
                <p><b>이메일:</b> {order.buyer_email}</p>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header">배송 정보</div>
              <div className="card-body">
                {delivery ? (
                  <>
                    <p><b>수령인:</b> {delivery.receiver_name}</p>
                    <p><b>전화:</b> {delivery.receiver_phone}</p>
                    <p>
                      <b>주소:</b><br />
                      {renderAddressWithBr(delivery.address || '')}
                    </p>
                    <p><b>택배사:</b> {delivery.courier}</p>
                    <p><b>운송장:</b> {delivery.tracking_number}</p>
                    <p><b>배송상태:</b> {delivery.delivery_status}</p>
                  </>
                ) : (
                  <p className="text-muted">배송 정보 없음</p>
                )}
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header">주문 상품</div>
              <div className="card-body p-0">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr className="table-light">
                      <th>상품명</th>
                      <th width="80">수량</th>
                      <th width="120">가격</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, idx) => (
                      <tr key={`${row.product_id || 'item'}-${idx}`}>
                        <td>{row.product_name}</td>
                        <td className="text-center">{row.quantity}</td>
                        <td className="text-end">{formatNumber(row.price)}원</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header">상태 변경 로그</div>
              <div className="card-body p-0">
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr className="table-light">
                      <th>변경일</th>
                      <th>이전</th>
                      <th>현재</th>
                      <th>처리자</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, idx) => (
                      <tr key={`${log.changed_at || 'log'}-${idx}`}>
                        <td>{log.changed_at}</td>
                        <td>{log.old_status}</td>
                        <td>{log.new_status}</td>
                        <td>{log.changed_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlatformAdminOrderView;